import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import {
  FiUser as User,
  FiMail as Mail,
  FiSun as Sun,
  FiMoon as Moon,
  FiCheck as Check,
  FiCheckCircle as CheckCircle,
  FiBriefcase as Building,
  FiAlertCircle as AlertCircle,
} from 'react-icons/fi';
import { apiCall } from '../services/realApi';

type SignupStep = 'initial' | 'company_check' | 'company_input' | 'submit';

interface FormData {
  name: string;
  email: string;
  company_name: string;
}

interface CompanyCheckResult {
  restricted_domain?: boolean;
  company_found?: boolean;
  company_required?: boolean;
  company_name?: string;
  client_id?: string;
  message?: string;
}

interface AnonymousSession {
  client_id?: string;
}

interface SignupResponse {
  success: boolean;
  password_setup_token?: string;
}

const SignUp: React.FC = () => {
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    company_name: '',
  });
  const [step, setStep] = useState<SignupStep>('initial');
  const [companyCheck, setCompanyCheck] = useState<CompanyCheckResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const { isDarkMode, toggleTheme } = useTheme();
  const navigate = useNavigate();

  const getAnonymousClientId = (): string | null => {
    const anonymousSession = localStorage.getItem('anonymous_session');
    if (anonymousSession) {
      try {
        const sessionData = JSON.parse(anonymousSession) as AnonymousSession;
        return sessionData.client_id ?? null;
      } catch {
        return null;
      }
    }
    return null;
  };

  const handleCheckCompany = async () => {
    if (!formData.name || !formData.email) {
      setError('Please enter your name and email');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const client_id = getAnonymousClientId();

      const response = await apiCall('/auth/check-company', {
        method: 'POST',
        requireAuth: false,
        body: JSON.stringify({
          email: formData.email,
          client_id,
        }),
      }) as CompanyCheckResult;

      setCompanyCheck(response);

      if (response.restricted_domain) {
        setError(response.message ?? 'This domain is restricted.');
        setStep('initial');
      } else if (response.company_found) {
        setStep('submit');
        await handleSubmit(null, response.company_name, response.client_id);
      } else if (response.company_required) {
        setStep('company_input');
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to check company. Please try again.';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (
    e: React.FormEvent<HTMLFormElement> | null,
    detectedCompanyName: string | undefined = undefined,
    detectedClientId: string | undefined = undefined,
  ) => {
    if (e) e.preventDefault();

    setLoading(true);
    setError(null);

    try {
      let client_id: string | undefined = detectedClientId;
      if (!client_id) {
        client_id = getAnonymousClientId() ?? undefined;
      }

      const response = await apiCall('/auth/signup', {
        method: 'POST',
        requireAuth: false,
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          company_name: detectedCompanyName ?? formData.company_name ?? undefined,
          client_id: client_id ?? undefined,
        }),
      }) as SignupResponse;

      if (response.success) {
        setSuccess(true);
        setTimeout(() => {
          navigate(`/set-password?token=${response.password_setup_token ?? ''}`);
        }, 2000);
      } else {
        setError('Unexpected response from server');
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to create account. Please try again.';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  if (success) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex flex-col justify-center py-12 sm:px-6 lg:px-8 transition-colors">
        <div className="absolute top-4 right-4">
          <button
            onClick={toggleTheme}
            className="p-2 text-gray-400 dark:text-gray-300 hover:text-gray-600 dark:hover:text-gray-100 transition-colors"
            title={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
          >
            {isDarkMode ? <Sun className="w-6 h-6" /> : <Moon className="w-6 h-6" />}
          </button>
        </div>

        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <div className="flex justify-center mb-6">
            <img
              src={isDarkMode ? '/lens-logo-dark.png' : '/lens-logo-light.png'}
              alt="Aivilo Lens"
              className="h-16 w-auto"
            />
          </div>
          <h2 className="text-center text-3xl font-bold text-gray-900 dark:text-white transition-colors">
            Account Created!
          </h2>
        </div>

        <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
          <div className="bg-white dark:bg-gray-800 py-8 px-4 shadow sm:rounded-lg sm:px-10 transition-colors">
            <div className="text-center">
              <div className="mx-auto w-16 h-16 bg-green-100 dark:bg-green-900/20 rounded-full flex items-center justify-center mb-4">
                <CheckCircle className="w-8 h-8 text-green-600 dark:text-green-400" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                Welcome to Aivilo Lens!
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
                Your account has been created successfully. Please check your email for a link to set your password.
              </p>
              <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 mb-6">
                <div className="flex items-center space-x-2 mb-2">
                  <Mail className="w-4 h-4 text-gray-400 dark:text-gray-500" />
                  <p className="text-xs text-gray-500 dark:text-gray-400">Email</p>
                </div>
                <p className="text-sm font-medium text-gray-900 dark:text-white">{formData.email}</p>
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Redirecting to password setup...
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const formSubmitHandler = step === 'company_input'
    ? handleSubmit
    : (e: React.FormEvent<HTMLFormElement>) => { e.preventDefault(); void handleCheckCompany(); };

  let submitButtonLabel: string;
  if (loading) {
    submitButtonLabel = 'Processing...';
  } else if (step === 'company_input') {
    submitButtonLabel = 'Create Account';
  } else {
    submitButtonLabel = 'Continue';
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex flex-col justify-center py-12 sm:px-6 lg:px-8 transition-colors">
      <div className="absolute top-4 right-4">
        <button
          onClick={toggleTheme}
          className="p-2 text-gray-400 dark:text-gray-300 hover:text-gray-600 dark:hover:text-gray-100 transition-colors"
          title={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
        >
          {isDarkMode ? <Sun className="w-6 h-6" /> : <Moon className="w-6 h-6" />}
        </button>
      </div>

      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="flex justify-center mb-6">
          <img
            src={isDarkMode ? '/lens-logo-dark.png' : '/lens-logo-light.png'}
            alt="Aivilo Lens"
            className="h-16 w-auto"
          />
        </div>
        <h2 className="text-center text-3xl font-bold text-gray-900 dark:text-white transition-colors">
          Create Your Account
        </h2>
        <p className="mt-2 text-center text-sm text-gray-600 dark:text-gray-400">
          Enter your details to get started
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white dark:bg-gray-800 py-8 px-4 shadow sm:rounded-lg sm:px-10 transition-colors">
          {error && (
            <div className="mb-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md p-4 flex items-start space-x-3">
              <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
            </div>
          )}

          {companyCheck?.company_found && (
            <div className="mb-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-md p-4">
              <p className="text-sm text-green-600 dark:text-green-400">
                <Check className="inline w-4 h-4 mr-1" />{companyCheck.message}
              </p>
            </div>
          )}

          <form onSubmit={formSubmitHandler} className="space-y-6">
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Full Name
              </label>
              <div className="mt-1 relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <User className="h-5 w-5 text-gray-400 dark:text-gray-500" />
                </div>
                <input
                  id="name"
                  name="name"
                  type="text"
                  required
                  disabled={step === 'company_input'}
                  value={formData.name}
                  onChange={handleChange}
                  className="appearance-none block w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  placeholder="John Doe"
                />
              </div>
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Email Address
              </label>
              <div className="mt-1 relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Mail className="h-5 w-5 text-gray-400 dark:text-gray-500" />
                </div>
                <input
                  id="email"
                  name="email"
                  type="email"
                  required
                  disabled={step === 'company_input'}
                  value={formData.email}
                  onChange={handleChange}
                  className="appearance-none block w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  placeholder="john.doe@company.com"
                />
              </div>
            </div>

            {step === 'company_input' && (
              <div>
                <label htmlFor="company_name" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Company Name
                </label>
                <div className="mt-1 relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Building className="h-5 w-5 text-gray-400 dark:text-gray-500" />
                  </div>
                  <input
                    id="company_name"
                    name="company_name"
                    type="text"
                    required
                    value={formData.company_name}
                    onChange={handleChange}
                    className="appearance-none block w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white transition-colors"
                    placeholder="Acme Corp"
                  />
                </div>
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  We couldn&apos;t find an organization for your email domain
                </p>
              </div>
            )}

            <div>
              <button
                type="submit"
                disabled={loading}
                className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {submitButtonLabel}
              </button>
            </div>
          </form>

          <div className="mt-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300 dark:border-gray-600"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-400">
                  Already have an account?
                </span>
              </div>
            </div>

            <div className="mt-6">
              <button
                onClick={() => navigate('/login')}
                className="w-full flex justify-center py-2 px-4 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
              >
                Log In
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignUp;
